import { cloneDeep } from 'lodash';
import { Layout } from 'react-grid-layout';

import {
  LayoutResponsiveBreakpoint,
  LayoutResponsiveDefinition,
} from '@work4all/components/lib/utils/react-grid-layout/react-grid-layout-models';

import { HomeWidget } from '../../../../../settings/validation/HomeWidget';
import { DEFAULT_HOME_WIDGET_LAYOUTS } from '../layouts';
import { WidgetPlacementOptions } from '../types';

/**
 * Checks if two widgets overlap in the grid
 */
export const doWidgetsOverlap = (widget1: Layout, widget2: Layout): boolean => {
  return !(
    widget1.x + widget1.w <= widget2.x ||
    widget2.x + widget2.w <= widget1.x ||
    widget1.y + widget1.h <= widget2.y ||
    widget2.y + widget2.h <= widget1.y
  );
};

/**
 * Creates layout using default predefined layouts with overlap detection
 */
export const createLayoutWithDefaults = (
  layout: Layout[],
  breakpoint: string,
  widget: HomeWidget
): Layout[] => {
  const defaultLayout = DEFAULT_HOME_WIDGET_LAYOUTS[breakpoint]?.find(
    (defaultWidget) => defaultWidget.i === widget.id
  );

  if (defaultLayout) {
    // Use predefined layout - find optimal placement

    // Check if default position is available, otherwise find next available spot
    let newWidget = { ...defaultLayout };
    let attempts = 0;
    const maxAttempts = 20;

    // Try to place widget without overlap
    while (attempts < maxAttempts) {
      let hasOverlap = false;

      for (const existingWidget of layout) {
        if (doWidgetsOverlap(newWidget, existingWidget)) {
          hasOverlap = true;
          break;
        }
      }

      if (!hasOverlap) {
        break;
      }

      // Move widget down to find available space
      newWidget = { ...newWidget, y: newWidget.y + 1 };
      attempts++;
    }

    return [...layout, newWidget];
  } else {
    // Fallback to auto-positioning logic
    return recalculateLayout(layout, widget);
  }
};

/**
 * Creates layout using custom placement options
 */
export const createLayoutWithCustom = (
  layout: Layout[],
  breakpoint: string,
  widget: HomeWidget,
  placementOptions: WidgetPlacementOptions
): Layout[] => {
  const customLayoutForBreakpoint =
    placementOptions?.customLayout?.[breakpoint as LayoutResponsiveBreakpoint];

  const newWidget: Layout = {
    i: widget.id,
    x: customLayoutForBreakpoint?.x ?? 1,
    y: customLayoutForBreakpoint?.y ?? 0,
    h: customLayoutForBreakpoint?.h ?? 3,
    w: customLayoutForBreakpoint?.w ?? 5,
    minW: customLayoutForBreakpoint?.minW ?? 2,
    minH: customLayoutForBreakpoint?.minH,
    maxW: customLayoutForBreakpoint?.maxW,
    maxH: customLayoutForBreakpoint?.maxH,
  };

  return [...layout, newWidget];
};

/**
 * Recalculates layout for a specific breakpoint with auto-positioning
 */
export const recalculateLayout = (
  layout: Layout[],
  widget: HomeWidget
): Layout[] => {
  const newWidget: Layout = {
    i: widget.id,
    x: 1,
    y: 0,
    h: 3,
    w: 5,
    minW: 2,
  };

  // Sort existing widgets by their current y position to maintain order
  const sortedExistingWidgets = [...layout].sort((a, b) => a.y - b.y);

  // Start positioning widgets after the new widget
  let currentY = newWidget.y + newWidget.h;

  // Recalculate positions for all existing widgets
  const repositionedWidgets = sortedExistingWidgets.map((existingWidget) => {
    const updatedWidget = {
      ...existingWidget,
      y: currentY,
    };

    // Move currentY down by the height of this widget for the next widget
    currentY += existingWidget.h;

    return updatedWidget;
  });

  // Combine new widget with repositioned widgets
  return [newWidget, ...repositionedWidgets];
};

/**
 * Interface for the result of adding a widget
 */
export interface AddWidgetResult {
  newWidgets: HomeWidget[];
  newLayouts: LayoutResponsiveDefinition;
}

/**
 * Main function to add a widget to layouts and widgets
 * Pure function that returns new state instead of mutating existing state
 */
export const addWidgetToLayouts = (
  widget: HomeWidget,
  currentWidgets: HomeWidget[],
  currentLayouts: LayoutResponsiveDefinition,
  placementOptions?: WidgetPlacementOptions
): AddWidgetResult => {
  const newWidgets = [...currentWidgets, widget];
  const newLayouts: LayoutResponsiveDefinition = cloneDeep(currentLayouts);

  // Process each breakpoint based on placement options
  for (const key in newLayouts) {
    if (newLayouts[key]) {
      if (placementOptions?.useDefaultLayouts) {
        newLayouts[key] = createLayoutWithDefaults(
          newLayouts[key],
          key,
          widget
        );
      } else if (placementOptions?.customLayout) {
        newLayouts[key] = createLayoutWithCustom(
          newLayouts[key],
          key,
          widget,
          placementOptions
        );
      } else {
        // Default behavior - auto-positioning with recalculation
        newLayouts[key] = recalculateLayout(newLayouts[key], widget);
      }
    }
  }

  return {
    newWidgets,
    newLayouts,
  };
};
